import { useState, useEffect, useRef, useCallback } from "react";

const API = window.location.origin;

const MODELS = {
  "gemini-3-flash": { label: "Gemini 3 Flash ✦",      color: "#06b6d4" },
  "gemini-3-pro":   { label: "Gemini 3 Pro ✦",        color: "#0284c7" },
  "gemini-flash":   { label: "Gemini 2.5 Flash",       color: "#34d399" },
  "o3-mini":        { label: "o3-mini (Azure)",         color: "#22c55e" },
  "haiku":          { label: "Claude Haiku 4.5",        color: "#f59e0b" },
  "sonnet":         { label: "Claude Sonnet 4.5",       color: "#3b82f6" },
  "opus":           { label: "Claude Opus 4.5",         color: "#a78bfa" },
  "opus-4-6":       { label: "Claude Opus 4.6",         color: "#c084fc" },
  "gpt-5-4":        { label: "OpenAI GPT-5.4",          color: "#f97316" },
};

// Question type chips config
const Q_TYPES = [
  { key: "assertion", label: "Assertion & Reasoning", color: "#f59e0b" },
  { key: "match",     label: "Match the Following",   color: "#ec4899" },
  { key: "statement", label: "Statement Based",       color: "#8b5cf6" },
];

export default function QCDashboard() {
  const [authed, setAuthed]       = useState(false);
  const [password, setPassword]   = useState("");
  const [loginError, setLoginError] = useState("");

  const [hierarchy, setHierarchy] = useState(null);

  // QC mode
  const [qcMode, setQcMode]       = useState("dual"); // "dual" | "solo"

  // Dual QC config
  const [modelA, setModelA]       = useState("gemini-flash");
  const [modelB, setModelB]       = useState("o3-mini");
  const [escModel, setEscModel]   = useState("opus-4-6");

  // Solo model config
  const [soloModel, setSoloModel] = useState("opus-4-6");

  // Question type filter & exam pattern
  const [questionTypes, setQuestionTypes] = useState([]); // [] = all; ["assertion","match"] = typed
  const [examPattern, setExamPattern]     = useState("both"); // "both" | "kvs" | "emrs"

  const [auditSource, setAuditSource]   = useState("both");
  const [auditCategory, setAuditCategory] = useState("all");
  const [auditSubjects, setAuditSubjects] = useState([]); // [] = all; ["mathematics","science"] = selected

  // Mock test & topic filters
  const [availableMocks, setAvailableMocks] = useState({}); // {subject: [{mock_number, exam_pattern, q_count}]}
  const [availableTopics, setAvailableTopics] = useState([]); // [{topic, count}]
  const [selectedMockNums, setSelectedMockNums] = useState([]); // [1, 2, 5]
  const [selectedTopics, setSelectedTopics] = useState([]); // ["topic1","topic2"]
  const [showMockFilter, setShowMockFilter] = useState(false);
  const [showTopicFilter, setShowTopicFilter] = useState(false);

  // Session state
  const [sessionId, setSessionId] = useState("");
  const [status, setStatus]       = useState(null);
  const [polling, setPolling]     = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  // Session Manager
  const [allSessions, setAllSessions]         = useState([]);
  const [showSessionMgr, setShowSessionMgr]   = useState(false);
  const [sessionMgrLoading, setSessionMgrLoading] = useState(false);
  const allSessRef = useRef(null);

  // History modal
  const [showHistory, setShowHistory]   = useState(false);
  const [historyData, setHistoryData]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all"); // "all" | "mock" | "objective"

  // Cost overview
  const [showCost, setShowCost]     = useState(false);
  const [costData, setCostData]     = useState(null);
  const [costLoading, setCostLoading] = useState(false);

  // Reset mocks section
  const [showReset, setShowReset]   = useState(false);
  const [resetExam, setResetExam]   = useState("");
  const [resetSubject, setResetSubject] = useState("all");
  const [resetPattern, setResetPattern] = useState("all");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult]   = useState(null);

  const storedPwd  = useRef("");
  const pollRef    = useRef(null);
  const sessionRef = useRef("");
  const pollFnRef  = useRef(null);

  // ── Auth ───────────────────────────────────────────────────────
  const handleLogin = async () => {
    setLoginError("");
    try {
      const r = await fetch(`${API}/api/qc/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const d = await r.json();
      if (d.success) {
        setAuthed(true);
        storedPwd.current = password;
        loadHierarchy(password);
      } else {
        setLoginError("Wrong password");
      }
    } catch { setLoginError("Connection error"); }
  };

  const loadHierarchy = async (pwd) => {
    try {
      const r = await fetch(`${API}/api/qc/hierarchy?password=${encodeURIComponent(pwd)}`);
      setHierarchy(await r.json());
    } catch {}
  };

  // Load available mock numbers & topics when category/subjects change
  const loadFilterOptions = useCallback(async () => {
    if (!storedPwd.current) return;
    try {
      const subjStr = auditSubjects.join(",");
      const r = await fetch(
        `${API}/api/qc/filter-options?password=${encodeURIComponent(storedPwd.current)}&category=${auditCategory}&subjects=${encodeURIComponent(subjStr)}`
      );
      const d = await r.json();
      setAvailableMocks(d.mock_numbers || {});
      setAvailableTopics(d.topics || []);
    } catch {}
  }, [auditCategory, auditSubjects]);

  useEffect(() => {
    if (authed) loadFilterOptions();
  }, [authed, auditCategory, auditSubjects, loadFilterOptions]);

  // ── Session ID ─────────────────────────────────────────────────
  const buildSid = useCallback(() => {
    const activeTypes = (questionTypes || []).filter(t => t && t !== "all");
    const typeSuffix    = activeTypes.length ? "_typed_" + [...activeTypes].sort().join("_") : "";
    const patternSuffix = examPattern && examPattern !== "both" ? `_${examPattern}` : "";
    const subjLabel = auditSubjects.length ? [...auditSubjects].sort().join("_") : "all";
    const mockSuffix = selectedMockNums.length ? "_m" + [...selectedMockNums].sort((a,b)=>a-b).join("_") : "";
    const topicSuffix = selectedTopics.length ? "_t" + [...selectedTopics].sort().join("_").slice(0,60) : "";
    return `audit_${auditSource}_${auditCategory}_${subjLabel}${patternSuffix}${typeSuffix}${mockSuffix}${topicSuffix}`;
  }, [auditSource, auditCategory, auditSubjects, examPattern, questionTypes, selectedMockNums, selectedTopics]);

  // ── Polling ────────────────────────────────────────────────────
  const pollStatus = useCallback(async (sid) => {
    const id = sid || sessionRef.current || buildSid();
    if (!id || !storedPwd.current) return;
    try {
      const r = await fetch(`${API}/api/qc/answer-audit/status?password=${encodeURIComponent(storedPwd.current)}&session_id=${encodeURIComponent(id)}`);
      const d = await r.json();
      setStatus(d);
      if (id !== sessionRef.current) {
        setSessionId(id);
        sessionRef.current = id;
      }
      if (d.status === "running") setPolling(true);
      if (["done", "error", "idle"].includes(d.status)) setPolling(false);
    } catch {}
  }, [buildSid]);

  useEffect(() => { pollFnRef.current = pollStatus; }, [pollStatus]);
  useEffect(() => { sessionRef.current = sessionId; }, [sessionId]);

  useEffect(() => {
    if (polling) {
      pollRef.current = setInterval(() => pollFnRef.current?.(), 3000);
      return () => clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [polling]);

  // Auto-check session on selection change
  useEffect(() => {
    if (authed) {
      const sid = buildSid();
      setSessionId(sid);
      sessionRef.current = sid;
      pollStatus(sid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, auditSource, auditCategory, auditSubjects, selectedMockNums, selectedTopics]);

  // ── Actions ────────────────────────────────────────────────────
  const handleStart = async (force = false) => {
    setActionLoading(force ? "force" : "start");
    const isSolo = qcMode === "solo";
    try {
      const r = await fetch(`${API}/api/qc/answer-audit/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: storedPwd.current,
          category: auditCategory,
          subject: auditSubjects.length === 1 ? auditSubjects[0] : "all",
          subjects: auditSubjects,
          source: auditSource,
          mode: isSolo ? "single" : "dual",
          model: soloModel,           // solo mode
          model_a: modelA,            // dual mode
          model_b: modelB,            // dual mode
          escalation_model: escModel, // dual mode
          force,
          question_types: questionTypes,
          exam_pattern: examPattern,
          mock_numbers: selectedMockNums,
          topics: selectedTopics,
        }),
      });
      const d = await r.json();
      if (d.success) {
        const sid = d.session_id;
        setSessionId(sid);
        sessionRef.current = sid;
        setPolling(true);
        pollStatus(sid);
      } else {
        alert(d.message || "Failed to start");
      }
    } catch { alert("Error starting audit"); }
    setActionLoading("");
  };

  const handlePause = async () => {
    setActionLoading("pause");
    try {
      await fetch(`${API}/api/qc/answer-audit/pause`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPwd.current, session_id: sessionRef.current }),
      });
      pollStatus();
    } catch { alert("Error pausing"); }
    setActionLoading("");
  };

  const handleReset = async () => {
    if (!window.confirm("Reset will clear all audit progress for this selection. Continue?")) return;
    setActionLoading("reset");
    try {
      await fetch(`${API}/api/qc/answer-audit/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPwd.current, session_id: sessionRef.current }),
      });
      setStatus(null);
      pollStatus();
    } catch { alert("Error resetting"); }
    setActionLoading("");
  };

  // ── Session Manager ───────────────────────────────────────────
  const loadAllSessions = async () => {
    if (!storedPwd.current) return;
    setSessionMgrLoading(true);
    try {
      const r = await fetch(`${API}/api/qc/answer-audit/list-all-sessions?password=${encodeURIComponent(storedPwd.current)}`);
      const d = await r.json();
      const sessions = d.sessions || [];
      setAllSessions(sessions);
      // Auto-show if multiple running or any running session exists
      const runningCount = sessions.filter(s => s.status === "running").length;
      if (runningCount >= 2) setShowSessionMgr(true);
    } catch {}
    setSessionMgrLoading(false);
  };

  const handleDeleteSession = async (sid) => {
    if (!window.confirm(`Delete session "${sid}"? This will cancel its background task and remove all progress.`)) return;
    try {
      await fetch(`${API}/api/qc/answer-audit/delete-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPwd.current, session_id: sid }),
      });
      loadAllSessions();
      if (sid === sessionRef.current) { setStatus(null); setPolling(false); }
    } catch { alert("Error deleting session"); }
  };

  const handleResumeSession = async (sess) => {
    // Switch the current session to this one and resume
    const sid = sess.session_id;
    setSessionId(sid);
    sessionRef.current = sid;
    setAuditSource(sess.source || auditSource);
    setAuditCategory(sess.category || auditCategory);
    if (sess.subjects && sess.subjects.length) {
      setAuditSubjects(sess.subjects);
    } else if (sess.subject && sess.subject !== "all") {
      setAuditSubjects([sess.subject]);
    }
    setShowSessionMgr(false);
    await pollStatus(sid);
    // If paused, auto-start resume
    if (sess.status === "paused") {
      await handleStart(false);
    }
  };

  // Poll all sessions every 8s when session manager is open
  useEffect(() => {
    if (!authed) return;
    loadAllSessions();
    const iv = setInterval(loadAllSessions, 8000);
    allSessRef.current = iv;
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const [clearLoading, setClearLoading] = useState(false);

  // ── Cost Overview ─────────────────────────────────────────────
  const loadCost = async () => {
    setCostLoading(true);
    setShowCost(true);
    try {
      const r = await fetch(`${API}/api/qc/cost-summary?password=${encodeURIComponent(storedPwd.current)}`);
      setCostData(await r.json());
    } catch { alert("Error loading cost data"); }
    setCostLoading(false);
  };

  // ── Clear History ──────────────────────────────────────────────
  const handleClearHistory = async () => {
    if (!window.confirm(`FULL QC RESET — This will:\n\n1. Delete all fix records\n2. Reset all QC sessions\n3. Unmark all questions so they get re-checked\n\nThis cannot be undone. Continue?`)) return;
    setClearLoading(true);
    try {
      const r = await fetch(`${API}/api/qc/clear-history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: storedPwd.current }),
      });
      const d = await r.json();
      if (d.success) {
        setHistoryData([]);
        alert(`Done!\n\n- ${d.deleted || 0} fix records deleted\n- ${d.sessions_reset || 0} sessions reset\n- ${d.audit_flags_cleared || 0} questions unmarked for re-check\n\nAll questions will be freshly checked on next run.`);
      } else {
        alert("Error: " + d.message);
      }
    } catch { alert("Error clearing history"); }
    setClearLoading(false);
  };

  // ── History ────────────────────────────────────────────────────
  const loadHistory = async () => {
    setHistoryLoading(true);
    setShowHistory(true);
    try {
      const r = await fetch(`${API}/api/qc/answer-audit/all-fixes?password=${encodeURIComponent(storedPwd.current)}&limit=50000`);
      const d = await r.json();
      const fixes = (d.fixes || []).sort((a, b) => (b.fixed_at || "").localeCompare(a.fixed_at || ""));
      setHistoryData(fixes);
    } catch { alert("Error loading history"); }
    setHistoryLoading(false);
  };

  const downloadCSV = async () => {
    let fixes = historyData;
    if (!fixes.length) return;
    const filtered = historyFilter === "all" ? fixes : fixes.filter(f => f.source === historyFilter);
    const headers = ['#','Subject','Topic','Source','Question','Option A','Option B','Option C','Option D','Before (Wrong)','After (Fixed)','Model','Fixed At'];
    const esc = v => `"${String(v || '').replace(/"/g, '""')}"`;
    const rows = filtered.map((f, i) => {
      const opts = f.options || [];
      return [
        i + 1,
        esc((f.subject || '').replace(/_/g, ' ')),
        esc(f.topic),
        esc(f.source),
        esc(f.question),
        esc(opts[0] || ''),
        esc(opts[1] || ''),
        esc(opts[2] || ''),
        esc(opts[3] || ''),
        esc(f.old_answer),
        esc(f.new_answer),
        esc(f.model),
        f.fixed_at || '',
      ].join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dual_qc_fixes_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Reset Mocks ────────────────────────────────────────────────
  const handleResetMocks = async () => {
    if (!resetExam) { alert("Select an exam type"); return; }
    const msg = `Delete all mock tests for:\nExam: ${resetExam.toUpperCase()}\nSubject: ${resetSubject}\nPattern: ${resetPattern}\n\nType "RESET" to confirm:`;
    if (window.prompt(msg) !== "RESET") { alert("Cancelled."); return; }
    setResetLoading(true); setResetResult(null);
    try {
      const r = await fetch(`${API}/api/mock-tests/admin/reset-mocks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ qc_password: storedPwd.current, exam_type: resetExam, subject: resetSubject, exam_pattern: resetPattern }),
      });
      const d = await r.json();
      setResetResult({ success: d.success, message: d.message || d.detail || (d.success ? "Done" : "Failed") });
    } catch (e) { setResetResult({ success: false, message: e.message }); }
    setResetLoading(false);
  };

  // ── Derived ────────────────────────────────────────────────────
  const isRunning = status?.status === "running";
  const isPaused  = status?.status === "paused";
  const isDone    = status?.status === "done";
  const statusColor = isRunning ? "#22c55e" : isPaused ? "#f59e0b" : isDone ? "#3b82f6" : "#ef4444";

  const filteredHistory = historyFilter === "all" ? historyData : historyData.filter(f => f.source === historyFilter);

  // ── Login Screen ───────────────────────────────────────────────
  if (!authed) {
    return (
      <div data-testid="qc-login-page" style={{ minHeight: "100vh", background: "#0a0a0f", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'JetBrains Mono', monospace" }}>
        <div style={{ background: "#12121a", border: "1px solid #1e1e2e", borderRadius: 12, padding: "48px 40px", width: 360, textAlign: "center" }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#f0f0f0", marginBottom: 4 }}>QC Dashboard</div>
          <div style={{ color: "#444", fontSize: 12, marginBottom: 32 }}>Dual Smart Quality Control</div>
          <input
            data-testid="qc-password-input"
            type="password"
            placeholder="Admin Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            style={inputStyle}
          />
          {loginError && <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 12 }}>{loginError}</div>}
          <button data-testid="qc-login-btn" onClick={handleLogin} style={{ ...btn("blue"), width: "100%", padding: "12px" }}>
            Login
          </button>
        </div>
      </div>
    );
  }

  // ── Dashboard ──────────────────────────────────────────────────
  return (
    <div data-testid="qc-dashboard" style={{ minHeight: "100vh", background: "#0a0a0f", color: "#e0e0e0", fontFamily: "'JetBrains Mono', monospace", padding: "24px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#f0f0f0" }}>Dual Smart QC</h1>
          <p style={{ color: "#444", fontSize: 11, margin: "4px 0 0" }}>
            Dual mode: two models check in parallel · Disagreements go to judge · Solo mode: single model, fastest path
          </p>
        </div>

        {/* Model Config */}
        <div style={card}>
          {/* Mode Toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "#666", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginRight: 4 }}>Mode</div>
            {[
              { key: "dual", label: "Dual QC", desc: "Two models + judge", color: "#3b82f6" },
              { key: "solo", label: "Solo Model", desc: "Single model only", color: "#a78bfa" },
            ].map(({ key, label, desc, color }) => (
              <button
                key={key}
                data-testid={`qc-mode-${key}`}
                onClick={() => setQcMode(key)}
                style={{
                  padding: "6px 16px",
                  borderRadius: 6,
                  border: `1px solid ${qcMode === key ? color : "#2a2a3a"}`,
                  background: qcMode === key ? `${color}22` : "transparent",
                  color: qcMode === key ? color : "#555",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.15s",
                }}
              >
                {label}
                <span style={{ display: "block", fontSize: 9, color: qcMode === key ? `${color}99` : "#333", fontWeight: 400, marginTop: 1 }}>{desc}</span>
              </button>
            ))}
          </div>

          {qcMode === "dual" ? (
            <>
              <div style={sectionLabel}>Models (Dual)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                {[
                  { label: "Model A — First Check", value: modelA, set: setModelA, color: "#22c55e", exclude: [modelB, escModel] },
                  { label: "Model B — Second Check", value: modelB, set: setModelB, color: "#3b82f6", exclude: [modelA, escModel] },
                  { label: "Judge — Arbitrates Disputes", value: escModel, set: setEscModel, color: "#c084fc", exclude: [] },
                ].map(({ label, value, set, color, exclude }) => (
                  <div key={label}>
                    <label style={{ display: "block", fontSize: 10, color, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>{label}</label>
                    <select value={value} onChange={e => set(e.target.value)} style={{ ...selectStyle, borderColor: `${color}44`, color }}>
                      {Object.entries(MODELS).filter(([k]) => !exclude.includes(k)).map(([k, m]) => (
                        <option key={k} value={k}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={sectionLabel}>Solo Model</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, alignItems: "end" }}>
                <div>
                  <label style={{ display: "block", fontSize: 10, color: "#a78bfa", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                    Model — Sole Checker
                  </label>
                  <select
                    data-testid="solo-model-select"
                    value={soloModel}
                    onChange={e => setSoloModel(e.target.value)}
                    style={{ ...selectStyle, borderColor: "#a78bfa44", color: "#a78bfa" }}
                  >
                    {Object.entries(MODELS).map(([k, m]) => (
                      <option key={k} value={k}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ padding: "8px 12px", background: "#12121a", borderRadius: 6, border: "1px solid #1e1e2e" }}>
                  <p style={{ margin: 0, fontSize: 10, color: "#555", lineHeight: 1.6 }}>
                    Solo mode runs a <strong style={{ color: "#a78bfa" }}>single model</strong> on all questions — no dispute resolution. Fastest way to QC a specific subject with a specific model. Useful for double-checking with a powerful model like Opus.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Scope Config */}
        <div style={{ ...card, marginTop: 14 }}>
          <div style={sectionLabel}>Scope</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
            <div>
              <label style={fieldLabel}>Source</label>
              <select data-testid="audit-source-select" value={auditSource} onChange={e => setAuditSource(e.target.value)} style={selectStyle}>
                <option value="both">Both (Practice + Mock)</option>
                <option value="objective">Practice Objective Only</option>
                <option value="mock">Full Mock Tests Only</option>
              </select>
            </div>
            <div>
              <label style={fieldLabel}>Category</label>
              <select data-testid="audit-category-select" value={auditCategory} onChange={e => { setAuditCategory(e.target.value); setAuditSubjects([]); setSelectedMockNums([]); setSelectedTopics([]); }} style={selectStyle}>
                <option value="all">All Categories</option>
                {hierarchy && Object.entries(hierarchy).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>Pattern</label>
              <select
                data-testid="audit-pattern-select"
                value={examPattern}
                onChange={e => setExamPattern(e.target.value)}
                style={selectStyle}
              >
                <option value="both">Both (KVS + EMRS)</option>
                <option value="kvs">KVS Only</option>
                <option value="emrs">EMRS Only</option>
              </select>
            </div>
          </div>

          {/* Multi-select Subjects */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #1a1a2a" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: "#555", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, minWidth: 70 }}>
                Subjects
              </span>
              <button
                data-testid="subject-all"
                onClick={() => { setAuditSubjects([]); setSelectedMockNums([]); setSelectedTopics([]); }}
                style={{
                  padding: "4px 14px", borderRadius: 20,
                  border: `1px solid ${auditSubjects.length === 0 ? "#06b6d4" : "#2a2a3a"}`,
                  background: auditSubjects.length === 0 ? "#06b6d422" : "transparent",
                  color: auditSubjects.length === 0 ? "#06b6d4" : "#444",
                  fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}
              >All Subjects</button>
              {auditCategory !== "all" && hierarchy && Object.entries(hierarchy[auditCategory]?.subjects || {}).map(([k, v]) => {
                const active = auditSubjects.includes(k);
                return (
                  <button
                    key={k}
                    data-testid={`subject-${k}`}
                    onClick={() => {
                      setAuditSubjects(prev =>
                        prev.includes(k) ? prev.filter(s => s !== k) : [...prev, k]
                      );
                      setSelectedMockNums([]);
                      setSelectedTopics([]);
                    }}
                    style={{
                      padding: "4px 14px", borderRadius: 20,
                      border: `1px solid ${active ? "#06b6d4" : "#2a2a3a"}`,
                      background: active ? "#06b6d422" : "transparent",
                      color: active ? "#06b6d4" : "#444",
                      fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {v.label}
                    {active && <span style={{ marginLeft: 4, fontSize: 9 }}>({(v.obj_count || 0) + (v.mock_count || 0)})</span>}
                  </button>
                );
              })}
              {auditSubjects.length > 0 && (
                <span style={{ fontSize: 10, color: "#06b6d4", marginLeft: 4 }}>
                  {auditSubjects.length} subject{auditSubjects.length > 1 ? "s" : ""} selected
                </span>
              )}
            </div>
          </div>

          {/* Mock Test Filter */}
          {(auditSource === "mock" || auditSource === "both") && Object.keys(availableMocks).length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1a1a2a" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 10, color: "#555", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, minWidth: 70 }}>
                  Mock Tests
                </span>
                <button
                  data-testid="mock-filter-toggle"
                  onClick={() => setShowMockFilter(v => !v)}
                  style={{
                    padding: "3px 12px", borderRadius: 20,
                    border: `1px solid ${selectedMockNums.length > 0 ? "#f97316" : "#2a2a3a"}`,
                    background: selectedMockNums.length > 0 ? "#f9731622" : "transparent",
                    color: selectedMockNums.length > 0 ? "#f97316" : "#444",
                    fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  {selectedMockNums.length > 0 ? `${selectedMockNums.length} mock(s) selected` : "All Mocks"} {showMockFilter ? "▲" : "▼"}
                </button>
                {selectedMockNums.length > 0 && (
                  <button
                    onClick={() => setSelectedMockNums([])}
                    style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid #ef444433", background: "transparent", color: "#ef4444", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                  >Clear</button>
                )}
              </div>
              {showMockFilter && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxHeight: 160, overflowY: "auto", padding: "6px 0" }}>
                  {Object.entries(availableMocks).map(([subj, mocks]) => (
                    mocks.map(m => {
                      const num = m.mock_number;
                      const active = selectedMockNums.includes(num);
                      return (
                        <button
                          key={`${subj}-${num}-${m.mock_id || m.exam_pattern}`}
                          data-testid={`mock-num-${num}`}
                          onClick={() => setSelectedMockNums(prev => prev.includes(num) ? prev.filter(n => n !== num) : [...prev, num])}
                          style={{
                            padding: "4px 10px", borderRadius: 6,
                            border: `1px solid ${active ? "#f97316" : "#2a2a3a"}`,
                            background: active ? "#f9731622" : "transparent",
                            color: active ? "#f97316" : "#555",
                            fontSize: 10, fontWeight: active ? 700 : 400, cursor: "pointer", fontFamily: "inherit",
                          }}
                        >
                          Mock {num} {m.exam_pattern ? `(${m.exam_pattern.toUpperCase()})` : ""} <span style={{ color: "#333", fontSize: 9 }}>{m.q_count}q</span>
                        </button>
                      );
                    })
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Topic Filter */}
          {availableTopics.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1a1a2a" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 10, color: "#555", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, minWidth: 70 }}>
                  Topics
                </span>
                <button
                  data-testid="topic-filter-toggle"
                  onClick={() => setShowTopicFilter(v => !v)}
                  style={{
                    padding: "3px 12px", borderRadius: 20,
                    border: `1px solid ${selectedTopics.length > 0 ? "#8b5cf6" : "#2a2a3a"}`,
                    background: selectedTopics.length > 0 ? "#8b5cf622" : "transparent",
                    color: selectedTopics.length > 0 ? "#8b5cf6" : "#444",
                    fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  {selectedTopics.length > 0 ? `${selectedTopics.length} topic(s) selected` : "All Topics"} {showTopicFilter ? "▲" : "▼"}
                </button>
                {selectedTopics.length > 0 && (
                  <button
                    onClick={() => setSelectedTopics([])}
                    style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid #ef444433", background: "transparent", color: "#ef4444", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}
                  >Clear</button>
                )}
              </div>
              {showTopicFilter && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxHeight: 200, overflowY: "auto", padding: "6px 0" }}>
                  {availableTopics.map(({ topic, count }) => {
                    const active = selectedTopics.includes(topic);
                    return (
                      <button
                        key={topic}
                        data-testid={`topic-${topic}`}
                        onClick={() => setSelectedTopics(prev => prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic])}
                        style={{
                          padding: "4px 10px", borderRadius: 6,
                          border: `1px solid ${active ? "#8b5cf6" : "#2a2a3a"}`,
                          background: active ? "#8b5cf622" : "transparent",
                          color: active ? "#8b5cf6" : "#555",
                          fontSize: 10, fontWeight: active ? 700 : 400, cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        {topic.replace(/_/g, " ")} <span style={{ color: "#333", fontSize: 9 }}>({count})</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Question Type Filter */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #1a1a2a" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, color: "#555", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, minWidth: 100 }}>
                Question Types
              </span>
              {/* All toggle */}
              <button
                data-testid="qtype-all"
                onClick={() => setQuestionTypes([])}
                style={{
                  padding: "4px 14px", borderRadius: 20, border: `1px solid ${questionTypes.length === 0 ? "#22c55e" : "#2a2a3a"}`,
                  background: questionTypes.length === 0 ? "#22c55e22" : "transparent",
                  color: questionTypes.length === 0 ? "#22c55e" : "#444",
                  fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}
              >All Types</button>
              {Q_TYPES.map(({ key, label, color }) => {
                const active = questionTypes.includes(key);
                return (
                  <button
                    key={key}
                    data-testid={`qtype-${key}`}
                    onClick={() => {
                      setQuestionTypes(prev =>
                        prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]
                      );
                    }}
                    style={{
                      padding: "4px 14px", borderRadius: 20,
                      border: `1px solid ${active ? color : "#2a2a3a"}`,
                      background: active ? `${color}22` : "transparent",
                      color: active ? color : "#444",
                      fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
              {questionTypes.length > 0 && (
                <span style={{ fontSize: 10, color: "#f59e0b", marginLeft: 4 }}>
                  Only {questionTypes.join(" + ")} questions will be processed
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          {!isRunning ? (
            <button
              data-testid="audit-start-btn"
              onClick={() => handleStart(false)}
              disabled={actionLoading === "start"}
              style={{ ...btn(isPaused ? "amber" : qcMode === "solo" ? "purple" : "green"), opacity: actionLoading === "start" ? 0.5 : 1 }}
            >
              {actionLoading === "start" ? "Starting..." : isPaused ? "Resume" : qcMode === "solo" ? `Start Solo QC (${MODELS[soloModel]?.label || soloModel})` : "Start Dual QC"}
            </button>
          ) : (
            <button data-testid="audit-pause-btn" onClick={handlePause} disabled={actionLoading === "pause"} style={btn("amber")}>
              {actionLoading === "pause" ? "Pausing..." : "Pause"}
            </button>
          )}

          {!isRunning && (
            <button
              data-testid="audit-force-btn"
              onClick={() => { if (window.confirm("Force Re-Audit will re-check ALL questions including previously audited ones. Continue?")) handleStart(true); }}
              disabled={actionLoading === "force"}
              style={{ ...btn("purple"), opacity: actionLoading === "force" ? 0.5 : 1 }}
            >
              {actionLoading === "force" ? "Starting..." : "Force Re-Audit"}
            </button>
          )}

          <button
            data-testid="audit-reset-btn"
            onClick={handleReset}
            disabled={isRunning || actionLoading === "reset"}
            style={{ ...btn("red"), opacity: isRunning ? 0.5 : 1 }}
          >
            Reset Progress
          </button>

          <button
            data-testid="audit-history-btn"
            onClick={loadHistory}
            style={btn("indigo")}
          >
            View History {status?.fixed ? `(${status.fixed} fixed)` : ""}
          </button>

          <button
            data-testid="audit-cost-btn"
            onClick={loadCost}
            style={btn("gray")}
            title="View cost consumed by Dual Smart QC sessions"
          >
            Cost Overview
          </button>

          {/* Session Manager button — shows badge if multiple running */}
          <button
            data-testid="session-manager-btn"
            onClick={() => { loadAllSessions(); setShowSessionMgr(v => !v); }}
            style={{
              ...btn("gray"),
              position: "relative",
              border: allSessions.filter(s => s.status === "running").length >= 2
                ? "1px solid #ef444466" : undefined,
              color: allSessions.filter(s => s.status === "running").length >= 2
                ? "#ef4444" : undefined,
            }}
            title="Manage all QC sessions"
          >
            Sessions
            {allSessions.filter(s => s.status === "running").length >= 2 && (
              <span style={{
                position: "absolute", top: -6, right: -6,
                background: "#ef4444", color: "#fff",
                fontSize: 9, fontWeight: 800,
                width: 16, height: 16, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {allSessions.filter(s => s.status === "running").length}
              </span>
            )}
          </button>

          <button
            onClick={() => pollStatus()}
            style={{ ...btn("gray"), marginLeft: "auto" }}
            title="Refresh status"
          >
            Refresh
          </button>
        </div>

        {/* ── Session Manager Panel ─────────────────────────────────── */}
        {showSessionMgr && (
          <div data-testid="session-manager-panel" style={{
            marginTop: 14,
            background: "#0a0a0f",
            border: allSessions.filter(s => s.status === "running").length >= 2
              ? "1px solid #ef444444" : "1px solid #1e1e2e",
            borderRadius: 10,
            overflow: "hidden",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #1a1a2a" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#f0f0f0", textTransform: "uppercase", letterSpacing: 1 }}>
                  Session Manager
                </span>
                {allSessions.filter(s => s.status === "running").length >= 2 && (
                  <span style={{ fontSize: 10, background: "#ef444415", color: "#ef4444", padding: "2px 8px", borderRadius: 4, border: "1px solid #ef444433" }}>
                    {allSessions.filter(s => s.status === "running").length} PARALLEL RUNNING — pick one to keep
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={loadAllSessions} style={{ ...btn("gray"), padding: "4px 10px", fontSize: 11 }}>
                  {sessionMgrLoading ? "..." : "Refresh"}
                </button>
                <button onClick={() => setShowSessionMgr(false)} style={{ background: "#1e1e2e", border: "1px solid #2a2a3a", color: "#888", fontSize: 14, cursor: "pointer", borderRadius: 6, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              </div>
            </div>

            {allSessions.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#444", fontSize: 12 }}>No sessions found.</div>
            ) : (
              <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {allSessions.map(sess => {
                  const sColor = sess.status === "running" ? "#22c55e" : sess.status === "paused" ? "#f59e0b" : sess.status === "done" ? "#3b82f6" : "#555";
                  const pct = sess.total > 0 ? Math.round(((sess.processed || 0) - (sess.skipped || 0)) / Math.max(1, sess.total - (sess.skipped || 0)) * 100) : 0;
                  const isThisSession = sess.session_id === sessionRef.current;
                  return (
                    <div key={sess.session_id} data-testid={`session-card-${sess.session_id}`} style={{
                      background: isThisSession ? "#22c55e08" : "#12121a",
                      border: `1px solid ${sess.status === "running" ? sColor + "44" : "#1e1e2e"}`,
                      borderRadius: 8,
                      padding: "12px 14px",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: sColor, display: "inline-block", animation: sess.status === "running" ? "pulse 1.5s infinite" : "none" }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: sColor, textTransform: "uppercase" }}>{sess.status}</span>
                            {sess.mode === "dual" && <span style={{ fontSize: 9, background: "#22c55e12", color: "#22c55e", padding: "1px 6px", borderRadius: 3, border: "1px solid #22c55e22" }}>DUAL</span>}
                            {sess.in_memory && <span style={{ fontSize: 9, background: "#ef444412", color: "#ef4444", padding: "1px 6px", borderRadius: 3, border: "1px solid #ef444422" }}>ACTIVE TASK</span>}
                            {isThisSession && <span style={{ fontSize: 9, color: "#555" }}>← current view</span>}
                          </div>
                          <div style={{ fontSize: 10, color: "#666", fontFamily: "monospace" }}>{sess.session_id}</div>
                          {(sess.model_a || sess.model_b) && (
                            <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>
                              {sess.model_a} + {sess.model_b}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#f0f0f0" }}>
                            {(sess.processed || 0) - (sess.skipped || 0)}/{Math.max(0, (sess.total || 0) - (sess.skipped || 0))} checked
                          </div>
                          <div style={{ fontSize: 10, color: "#22c55e" }}>{sess.fixed || 0} fixed · {sess.disagreements || 0} disputed</div>
                          {sess.updated_at && (
                            <div style={{ fontSize: 9, color: "#333", marginTop: 2 }}>
                              {new Date(sess.updated_at).toLocaleTimeString()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Progress bar */}
                      {sess.total > 0 && (
                        <div style={{ height: 3, background: "#1a1a2a", borderRadius: 2, overflow: "hidden", marginBottom: 10 }}>
                          <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: sColor, transition: "width 0.5s ease" }} />
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: "flex", gap: 6 }}>
                        {sess.status === "paused" && (
                          <button
                            data-testid={`resume-session-${sess.session_id}`}
                            onClick={() => handleResumeSession(sess)}
                            style={{ ...btn("green"), padding: "4px 12px", fontSize: 11 }}
                          >
                            Resume This
                          </button>
                        )}
                        {sess.status === "running" && (
                          <button
                            data-testid={`pause-session-${sess.session_id}`}
                            onClick={async () => {
                              await fetch(`${API}/api/qc/answer-audit/pause`, {
                                method: "POST", headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ password: storedPwd.current, session_id: sess.session_id }),
                              });
                              loadAllSessions(); pollStatus();
                            }}
                            style={{ ...btn("amber"), padding: "4px 12px", fontSize: 11 }}
                          >
                            Pause
                          </button>
                        )}
                        <button
                          data-testid={`view-session-${sess.session_id}`}
                          onClick={() => {
                            sessionRef.current = sess.session_id;
                            setSessionId(sess.session_id);
                            pollStatus(sess.session_id);
                            setShowSessionMgr(false);
                          }}
                          style={{ ...btn("indigo"), padding: "4px 12px", fontSize: 11 }}
                        >
                          View
                        </button>
                        <button
                          data-testid={`delete-session-${sess.session_id}`}
                          onClick={() => handleDeleteSession(sess.session_id)}
                          style={{ ...btn("red"), padding: "4px 12px", fontSize: 11, marginLeft: "auto" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Live Status */}
        {status && status.status !== "idle" && (
          <div data-testid="audit-live-status" style={{
            marginTop: 18,
            background: "#12121a",
            border: `1px solid ${statusColor}33`,
            borderRadius: 10,
            padding: 20,
          }}>
            {/* Status row */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: statusColor, display: "inline-block", animation: isRunning ? "pulse 1.5s infinite" : "none" }} />
              <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: statusColor }}>{status.status}</span>
              {status.mode === "dual" && (
                <span style={{ fontSize: 10, background: "#22c55e15", color: "#22c55e", padding: "2px 8px", borderRadius: 4, border: "1px solid #22c55e33" }}>DUAL SMART</span>
              )}
              {status.updated_at && (
                <span style={{ fontSize: 10, color: "#333", marginLeft: "auto" }}>
                  {new Date(status.updated_at).toLocaleTimeString()}
                </span>
              )}
            </div>

            {/* Summary */}
            <div style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.6 }}>{status.summary}</div>

            {/* Stats row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 14 }}>
              <StatBox label="Total"     value={Math.max(0, (status.total || 0) - (status.skipped || 0))}  color="#555" />
              <StatBox label="Checked"   value={Math.max(0, (status.processed || 0) - (status.skipped || 0))}  color="#3b82f6" />
              <StatBox label="Fixed"     value={status.fixed || 0}       color="#22c55e" />
              <StatBox label="Agreed"    value={status.agreements || 0}  color="#06b6d4" />
              <StatBox label="Disputed→Judged"  value={status.disagreements || 0} color="#f59e0b" title="Both models disagreed → sent to Judge (Opus). Most stay as-is if judge agrees with original." />
            </div>

            {/* Extra stats row: wrong found + skipped */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: (status.mode === "dual" && (status.escalation_pending || status.escalated_fixed)) ? 14 : 0 }}>
              <StatBox label="Wrong Found"   value={status.wrong_found || 0}  color="#ef4444" />
              <StatBox label="Escalated"     value={status.escalated_fixed || 0} color="#c084fc" />
              <div style={{ background: "#0a0a0f", borderRadius: 8, padding: "10px 8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ textAlign: "center", flex: 1 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#444" }}>{status.skipped || 0}</div>
                    <div style={{ fontSize: 9, color: "#333", marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>Skipped</div>
                  </div>
                  <div style={{ fontSize: 10, color: "#2a2a3a", lineHeight: 1.4, flex: 2 }}>
                    Already audited in a prior run. Use <em style={{ color: "#7c3aed" }}>Force Re-Audit</em> to re-check all.
                  </div>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            {status.total > 0 && (() => {
              const actualTotal = Math.max(1, (status.total || 0) - (status.skipped || 0));
              const actualChecked = Math.max(0, (status.processed || 0) - (status.skipped || 0));
              const pct = Math.round(actualChecked / actualTotal * 100);
              return (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#444", marginBottom: 4 }}>
                  <span>Progress</span>
                  <span>{actualChecked} / {actualTotal} · {pct}%</span>
                </div>
                <div style={{ height: 6, background: "#1a1a2a", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${Math.min(pct, 100)}%`,
                    background: isRunning ? "linear-gradient(90deg, #22c55e, #3b82f6)" : statusColor,
                    borderRadius: 3,
                    transition: "width 0.5s ease",
                  }} />
                </div>
              </div>
              );
            })()}

            {/* Subject Worker Progress — shows all 6 concurrent workers */}
            {status.subject_progress && Object.keys(status.subject_progress).length > 0 && (() => {
              const entries = Object.entries(status.subject_progress);
              const running = entries.filter(([, sp]) => sp.status === "running");
              const done    = entries.filter(([, sp]) => sp.status === "done");
              const pending = entries.filter(([, sp]) => !sp.status || sp.status === "pending");
              const sorted  = [...running, ...pending, ...done];
              return (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 10, color: "#444", textTransform: "uppercase", letterSpacing: 1.5 }}>
                      Parallel Workers
                    </span>
                    {running.length > 0 && (
                      <span style={{ fontSize: 10, background: "#22c55e15", color: "#22c55e", padding: "1px 7px", borderRadius: 4, border: "1px solid #22c55e33" }}>
                        {running.length} ACTIVE
                      </span>
                    )}
                    {done.length > 0 && (
                      <span style={{ fontSize: 10, color: "#3b82f6" }}>{done.length} done</span>
                    )}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
                    {sorted.map(([subj, sp]) => {
                      const pct      = sp.total > 0 ? Math.round((sp.checked || 0) / sp.total * 100) : 0;
                      const isActive = sp.status === "running";
                      const isDone   = sp.status === "done";
                      return (
                        <div key={subj} style={{
                          background: isActive ? "#22c55e08" : isDone ? "#3b82f608" : "#0a0a0f",
                          border: `1px solid ${isActive ? "#22c55e33" : isDone ? "#3b82f622" : "#1a1a2a"}`,
                          borderRadius: 6,
                          padding: "7px 10px",
                          transition: "all 0.3s ease",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                            <span style={{
                              fontSize: 11,
                              color: isActive ? "#22c55e" : isDone ? "#3b82f6" : "#555",
                              fontWeight: isActive ? 700 : 400,
                              display: "flex", alignItems: "center", gap: 5,
                            }}>
                              {isActive && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 1.5s infinite" }} />}
                              {isDone && <span style={{ color: "#3b82f6", fontSize: 10 }}>✓</span>}
                              {subj.replace(/_/g, " ")}
                            </span>
                            <span style={{ fontSize: 10, color: "#444", fontVariantNumeric: "tabular-nums" }}>
                              {`${sp.checked || 0}/${sp.total || 0}`}
                            </span>
                          </div>
                          <div style={{ height: 2, background: "#1a1a2a", borderRadius: 1, overflow: "hidden" }}>
                            <div style={{
                              height: "100%",
                              width: `${Math.min(pct, 100)}%`,
                              background: isActive ? "#22c55e" : isDone ? "#3b82f6" : "#2a2a3a",
                              transition: "width 0.5s ease",
                            }} />
                          </div>
                          {(sp.wrong > 0 || sp.fixed > 0 || sp.disputes > 0) && (
                            <div style={{ fontSize: 9, color: "#555", marginTop: 3 }}>
                              {sp.wrong > 0 && <span style={{ color: "#ef444488" }}>{sp.wrong} wrong </span>}
                              {sp.fixed > 0 && <span style={{ color: "#22c55e88" }}>· {sp.fixed} fixed </span>}
                              {sp.disputes > 0 && <span style={{ color: "#f59e0b88" }}>· {sp.disputes} judged</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Reset Mock Tests */}
        <div style={{ marginTop: 32, background: "#0a0a0f", border: "1px solid #1e1e2e", borderRadius: 12, overflow: "hidden" }}>
          <button
            data-testid="toggle-reset-section"
            onClick={() => { setShowReset(v => !v); setResetResult(null); }}
            style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", background: "transparent", border: "none", cursor: "pointer", color: "#ef4444", fontWeight: 700, fontSize: 13, letterSpacing: 0.5 }}
          >
            <span>RESET MOCK TESTS</span>
            <span style={{ fontSize: 16, transform: showReset ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▼</span>
          </button>

          {showReset && (
            <div style={{ padding: "0 20px 20px", borderTop: "1px solid #1a1a2a" }}>
              <p style={{ color: "#666", fontSize: 12, marginBottom: 16, marginTop: 14 }}>Permanently delete all stored mock tests for a specific exam/subject/pattern. Students can regenerate fresh mocks after.</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={fieldLabel}>Exam Type</label>
                  <select data-testid="reset-exam-type" value={resetExam} onChange={e => { setResetExam(e.target.value); setResetSubject("all"); setResetResult(null); }} style={selectStyle}>
                    <option value="">Select exam...</option>
                    {hierarchy && Object.entries(hierarchy).map(([k, v]) => (
                      <option key={k} value={k}>{v.label || k.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Subject</label>
                  <select data-testid="reset-subject" value={resetSubject} onChange={e => { setResetSubject(e.target.value); setResetResult(null); }} disabled={!resetExam} style={selectStyle}>
                    <option value="all">All Subjects</option>
                    {resetExam && hierarchy && Object.entries(hierarchy[resetExam]?.subjects || {}).map(([k, v]) => (
                      <option key={k} value={k}>{v.label || k}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={fieldLabel}>Pattern</label>
                  <select data-testid="reset-pattern" value={resetPattern} onChange={e => { setResetPattern(e.target.value); setResetResult(null); }} style={selectStyle}>
                    <option value="all">All Patterns</option>
                    <option value="kvs">KVS</option>
                    <option value="emrs">EMRS</option>
                  </select>
                </div>
              </div>
              {resetResult && (
                <div data-testid="reset-result" style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, background: resetResult.success ? "#22c55e15" : "#ef444415", border: `1px solid ${resetResult.success ? "#22c55e44" : "#ef444444"}`, color: resetResult.success ? "#22c55e" : "#ef4444", fontSize: 12 }}>
                  {resetResult.message}
                </div>
              )}
              <button data-testid="reset-mocks-btn" onClick={handleResetMocks} disabled={!resetExam || resetLoading} style={{ ...btn("red"), opacity: (!resetExam || resetLoading) ? 0.5 : 1 }}>
                {resetLoading ? "Deleting..." : "Reset Mock Tests"}
              </button>
            </div>
          )}
        </div>

      </div>

      {/* ── Cost Modal ───────────────────────────────────────────── */}
      {showCost && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}>
          <div style={{ background: "#0f0f18", border: "1px solid #c084fc33", borderRadius: 14, width: "100%", maxWidth: 780, boxShadow: "0 24px 80px rgba(0,0,0,0.7)" }}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #1e1e2e" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f0f0f0" }}>Cost Overview — Dual Smart QC</h3>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#444" }}>Estimated cost per session based on questions processed × model pricing</p>
              </div>
              <button
                onClick={() => setShowCost(false)}
                style={{ background: "#1e1e2e", border: "1px solid #2a2a3a", color: "#888", fontSize: 16, cursor: "pointer", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}
              >×</button>
            </div>

            <div style={{ padding: "20px 24px" }}>
              {costLoading ? (
                <div style={{ textAlign: "center", padding: 48, color: "#555" }}>Loading cost data...</div>
              ) : !costData ? null : (
                <>
                  {/* Grand Total */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 24 }}>
                    <div style={{ background: "#c084fc12", border: "1px solid #c084fc33", borderRadius: 10, padding: "16px", textAlign: "center" }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: "#c084fc" }}>${costData.grand_total_usd?.toFixed(4) || "0.0000"}</div>
                      <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>Total Spent</div>
                    </div>
                    <div style={{ background: "#22c55e12", border: "1px solid #22c55e33", borderRadius: 10, padding: "16px", textAlign: "center" }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: "#22c55e" }}>${costData.dual_qc?.total_cost_usd?.toFixed(4) || "0.0000"}</div>
                      <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>Dual Smart QC</div>
                      <div style={{ fontSize: 10, color: "#3a3a4a", marginTop: 2 }}>{costData.dual_qc?.total_sessions || 0} sessions · {costData.dual_qc?.total_fixed || 0} fixes</div>
                    </div>
                    <div style={{ background: "#3b82f612", border: "1px solid #3b82f633", borderRadius: 10, padding: "16px", textAlign: "center" }}>
                      <div style={{ fontSize: 26, fontWeight: 800, color: "#3b82f6" }}>${costData.auto_qc?.total_cost_usd?.toFixed(4) || "0.0000"}</div>
                      <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>Auto-QC (New Mocks)</div>
                      <div style={{ fontSize: 10, color: "#3a3a4a", marginTop: 2 }}>{costData.auto_qc?.total_mocks_qcd || 0} mocks · {costData.auto_qc?.total_fixed || 0} fixes</div>
                    </div>
                  </div>

                  {/* Dual QC Sessions */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 11, color: "#c084fc", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 12 }}>Dual Smart QC Sessions</div>
                    {(!costData.dual_qc?.sessions?.length) ? (
                      <div style={{ color: "#444", fontSize: 12, textAlign: "center", padding: "20px" }}>No completed sessions yet</div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid #1e1e2e" }}>
                            <th style={{ ...th, color: "#c084fc" }}>Models</th>
                            <th style={th}>Checked</th>
                            <th style={th}>Disputes</th>
                            <th style={{ ...th, color: "#22c55e" }}>Fixed</th>
                            <th style={{ ...th, color: "#f59e0b" }}>Est. Cost</th>
                            <th style={th}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costData.dual_qc.sessions.map((s, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #141420", background: i % 2 ? "#0a0a12" : "transparent" }}>
                              <td style={{ ...td, color: "#a78bfa", fontSize: 11 }}>{s.models}</td>
                              <td style={{ ...td, textAlign: "center" }}>{s.processed}</td>
                              <td style={{ ...td, textAlign: "center", color: "#f59e0b" }}>{s.disagreements}</td>
                              <td style={{ ...td, textAlign: "center", color: "#22c55e", fontWeight: 700 }}>{s.fixed}</td>
                              <td style={{ ...td, textAlign: "center", color: "#f59e0b", fontWeight: 700 }}>${s.est_cost_usd?.toFixed(4)}</td>
                              <td style={{ ...td, color: "#555", fontSize: 10 }}>
                                {s.updated_at ? new Date(s.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pricing Reference */}
                  <div style={{ background: "#0a0a0f", border: "1px solid #1e1e2e", borderRadius: 8, padding: "14px 16px" }}>
                    <div style={{ fontSize: 10, color: "#444", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>Model Pricing (per question)</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                      {[
                        { model: "Gemini Flash", cost: "$0.000008", color: "#06b6d4" },
                        { model: "o3-mini", cost: "$0.000080", color: "#22c55e" },
                        { model: "Claude Haiku", cost: "$0.000010", color: "#f59e0b" },
                        { model: "Claude Sonnet", cost: "$0.000060", color: "#3b82f6" },
                        { model: "Opus 4.5", cost: "$0.000800", color: "#a78bfa" },
                        { model: "Opus 4.6", cost: "$0.001000", color: "#c084fc" },
                        { model: "GPT-5.4", cost: "$0.000400", color: "#f97316" },
                        { model: "Auto-QC", cost: "$0.000300/call", color: "#555" },
                      ].map(({ model, cost, color }) => (
                        <div key={model} style={{ textAlign: "center", padding: "8px", borderRadius: 6, background: "#12121a" }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color }}>{model}</div>
                          <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{cost}</div>
                        </div>
                      ))}
                    </div>
                    <p style={{ fontSize: 10, color: "#333", marginTop: 10, fontStyle: "italic" }}>* Estimates based on avg ~500 tokens per question. Actual costs may vary.</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── History Modal ─────────────────────────────────────────── */}
      {showHistory && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px 16px", overflowY: "auto" }}>
          <div style={{ background: "#0f0f18", border: "1px solid #22c55e33", borderRadius: 14, width: "100%", maxWidth: 1100, boxShadow: "0 24px 80px rgba(0,0,0,0.7)" }}>

            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #1e1e2e" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f0f0f0" }}>
                  Fix History
                  {!historyLoading && <span style={{ fontSize: 12, color: "#555", fontWeight: 400, marginLeft: 10 }}>· {filteredHistory.length} corrections</span>}
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#444" }}>Persisted across sessions · survives resets</p>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                {/* Filter */}
                <select value={historyFilter} onChange={e => setHistoryFilter(e.target.value)} style={{ ...selectStyle, fontSize: 11, padding: "6px 10px", width: "auto" }}>
                  <option value="all">All Sources ({historyData.length})</option>
                  <option value="mock">Mock Tests ({historyData.filter(f => f.source === "mock").length})</option>
                  <option value="objective">Practice ({historyData.filter(f => f.source === "objective").length})</option>
                </select>
                {filteredHistory.length > 0 && (
                  <button
                    data-testid="audit-download-csv-btn"
                    onClick={downloadCSV}
                    style={{ background: "#22c55e18", border: "1px solid #22c55e44", borderRadius: 8, color: "#22c55e", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "8px 16px" }}
                  >
                    Download CSV
                  </button>
                )}
                {historyData.length > 0 && (
                  <button
                    data-testid="audit-clear-history-btn"
                    onClick={handleClearHistory}
                    disabled={clearLoading}
                    style={{ background: "#ef444418", border: "1px solid #ef444444", borderRadius: 8, color: "#ef4444", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "8px 16px", opacity: clearLoading ? 0.5 : 1 }}
                  >
                    {clearLoading ? "Clearing..." : "Clear Old History"}
                  </button>
                )}
                <button
                  data-testid="audit-close-history"
                  onClick={() => setShowHistory(false)}
                  style={{ background: "#1e1e2e", border: "1px solid #2a2a3a", color: "#888", fontSize: 16, cursor: "pointer", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div style={{ padding: "0 0 20px" }}>
              {historyLoading ? (
                <div style={{ textAlign: "center", padding: 60, color: "#555", fontSize: 13 }}>Loading fixes...</div>
              ) : filteredHistory.length === 0 ? (
                <div style={{ textAlign: "center", padding: 60, color: "#444", fontSize: 13 }}>No corrections recorded yet. Run Dual Smart QC to audit questions.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#12121a", borderBottom: "2px solid #1e1e2e" }}>
                        <th style={th}>#</th>
                        <th style={th}>Subject</th>
                        <th style={{ ...th, minWidth: 320 }}>Question</th>
                        <th style={{ ...th, color: "#ef4444" }}>Before</th>
                        <th style={{ ...th, color: "#22c55e" }}>After (Fixed)</th>
                        <th style={th}>Source</th>
                        <th style={th}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((f, i) => {
                        const opts = f.options || [];
                        const oldLetter = f.old_idx >= 0 ? String.fromCharCode(65 + f.old_idx) : "?";
                        const newLetter = f.new_idx >= 0 ? String.fromCharCode(65 + f.new_idx) : "?";
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid #141420", background: i % 2 ? "#0a0a12" : "transparent" }}>
                            <td style={{ ...td, color: "#555", textAlign: "center", fontWeight: 600 }}>{i + 1}</td>

                            <td style={{ ...td, verticalAlign: "top", paddingTop: 10 }}>
                              <div style={{ color: "#a78bfa", fontWeight: 700, fontSize: 10 }}>{(f.subject || "").replace(/_/g, ' ').toUpperCase()}</div>
                              <div style={{ color: "#444", fontSize: 10, marginTop: 2 }}>{f.topic}</div>
                            </td>

                            <td style={{ ...td, verticalAlign: "top", paddingTop: 10, maxWidth: 400 }}>
                              <div style={{ color: "#ddd", lineHeight: 1.5, marginBottom: 6 }}>{f.question}</div>
                              {opts.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                  {opts.map((opt, oi) => {
                                    const l = String.fromCharCode(65 + oi);
                                    const isOld = oi === f.old_idx;
                                    const isNew = oi === f.new_idx;
                                    return (
                                      <div key={oi} style={{ display: "flex", gap: 6, padding: "2px 7px", borderRadius: 4, background: isNew ? "#22c55e12" : isOld ? "#ef444412" : "transparent", border: `1px solid ${isNew ? "#22c55e33" : isOld ? "#ef444433" : "transparent"}` }}>
                                        <span style={{ fontWeight: 700, fontSize: 10, minWidth: 16, color: isNew ? "#22c55e" : isOld ? "#ef4444" : "#444" }}>{l})</span>
                                        <span style={{ color: isNew ? "#86efac" : isOld ? "#fca5a5" : "#777", fontSize: 11 }}>{opt}</span>
                                        {isNew && <span style={{ marginLeft: "auto", fontSize: 9, color: "#22c55e", fontWeight: 700 }}>CORRECT</span>}
                                        {isOld && !isNew && <span style={{ marginLeft: "auto", fontSize: 9, color: "#ef4444" }}>WRONG</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </td>

                            <td style={{ ...td, verticalAlign: "top", paddingTop: 10 }}>
                              <div style={{ display: "inline-flex", gap: 6, background: "#ef444420", border: "1px solid #ef444440", borderRadius: 6, padding: "4px 10px" }}>
                                <span style={{ color: "#ef4444", fontWeight: 800, fontSize: 14 }}>{oldLetter}</span>
                                <span style={{ color: "#fca5a5", fontSize: 11 }}>{String(f.old_answer || "").slice(0, 50)}</span>
                              </div>
                            </td>

                            <td style={{ ...td, verticalAlign: "top", paddingTop: 10 }}>
                              <div style={{ display: "inline-flex", gap: 6, background: "#22c55e20", border: "1px solid #22c55e40", borderRadius: 6, padding: "4px 10px", marginBottom: 4 }}>
                                <span style={{ color: "#22c55e", fontWeight: 800, fontSize: 14 }}>{newLetter}</span>
                                <span style={{ color: "#86efac", fontSize: 11 }}>{String(f.new_answer || "").slice(0, 50)}</span>
                              </div>
                              {f.explanation && (
                                <div style={{ fontSize: 10, color: "#555", fontStyle: "italic", lineHeight: 1.4, maxWidth: 180 }}>{String(f.explanation).slice(0, 100)}</div>
                              )}
                            </td>

                            <td style={{ ...td, verticalAlign: "top", paddingTop: 10 }}>
                              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, fontWeight: 600, background: f.source === "mock" ? "#3b82f620" : "#8b5cf620", color: f.source === "mock" ? "#93c5fd" : "#c4b5fd" }}>
                                {f.source === "mock" ? "MOCK" : "PRACTICE"}
                              </span>
                              {f.model && <div style={{ fontSize: 9, color: "#444", marginTop: 4 }}>{f.model}</div>}
                            </td>

                            <td style={{ ...td, verticalAlign: "top", paddingTop: 10, fontSize: 10, color: "#555", whiteSpace: "nowrap" }}>
                              {f.fixed_at ? new Date(f.fixed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "-"}
                              <div style={{ fontSize: 9, color: "#3a3a4a" }}>{f.fixed_at ? new Date(f.fixed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      `}</style>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: "#0a0a0f", borderRadius: 8, padding: "12px 8px", textAlign: "center" }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 9, color: "#444", marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
    </div>
  );
}

// ── Style helpers ──────────────────────────────────────────────────────────

const card = {
  background: "#12121a",
  border: "1px solid #1e1e2e",
  borderRadius: 10,
  padding: 18,
};

const sectionLabel = {
  fontSize: 10,
  color: "#444",
  textTransform: "uppercase",
  letterSpacing: 1.5,
  marginBottom: 12,
};

const fieldLabel = {
  display: "block",
  fontSize: 10,
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: 1,
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  background: "#0a0a0f",
  border: "1px solid #2a2a3a",
  borderRadius: 8,
  color: "#e0e0e0",
  fontSize: 14,
  outline: "none",
  marginBottom: 14,
  boxSizing: "border-box",
};

const selectStyle = {
  width: "100%",
  padding: "9px 12px",
  background: "#0a0a0f",
  border: "1px solid #2a2a3a",
  borderRadius: 6,
  color: "#e0e0e0",
  fontSize: 12,
  outline: "none",
  cursor: "pointer",
};

const COLORS = {
  green:  { bg: "#22c55e",  hover: "#16a34a" },
  amber:  { bg: "#f59e0b",  hover: "#d97706" },
  purple: { bg: "#7c3aed",  hover: "#6d28d9" },
  red:    { bg: "#ef4444",  hover: "#dc2626" },
  indigo: { bg: "#6366f1",  hover: "#4f46e5" },
  blue:   { bg: "#3b82f6",  hover: "#2563eb" },
  gray:   { bg: "#1e1e2e",  hover: "#2a2a3a" },
};

function btn(color) {
  const c = COLORS[color] || COLORS.gray;
  return {
    padding: "10px 22px",
    border: "none",
    borderRadius: 6,
    color: color === "gray" ? "#888" : "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    background: c.bg,
    transition: "opacity 0.15s",
  };
}

const th = {
  textAlign: "left",
  padding: "8px 12px",
  color: "#555",
  fontWeight: 600,
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 1,
};

const td = {
  padding: "8px 12px",
  color: "#ccc",
};
